import { useCallback, useEffect, createContext, useRef } from 'react';
import { RouterProvider } from 'react-router-dom';
import AppRouter from './Router';
import { Provider } from 'react-redux';
import { store } from './store';
import { Modal } from './shared/ui';

import { useAppSelector, useAppDispatch } from '@/shared/hooks/store';
import { selectAuthState } from '@/store/slices/auth/selectors';
import { useCookies } from 'react-cookie';
import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';
import { GtmEvent } from '@/shared/types/enums';
import { userDetails } from '@/store/slices/onboarding/selectors';
import { resetPersistSlice } from './store/helpers';
import { selectMicEvents, selectMicModalOpen } from '@/store/slices/v-t-s/selectors';
import { clearVoiceToSpeechTracking, closeModal } from '@/store/slices/v-t-s';
import { usePostHog } from 'posthog-js/react'
// import Intercom, { boot, shutdown, show, onShow, onHide } from '@intercom/messenger-js-sdk';
import { selectAccessibilityTop } from './store/slices/floating-icons-state/selectors';
import _ from 'lodash';

const App = () => {
    return (
        <Provider store={store}>
            <RouterComponent />
        </Provider>
    );
};

export default App;
interface IntercomContextInterface {
    shutDownIntercomContext: () => void;
    bootIntercomContext: () => void;
}
// intercom chat app context
export const IntercomContext = createContext<IntercomContextInterface>({ shutDownIntercomContext: _.noop, bootIntercomContext: _.noop });


const RouterComponent = () => {
    const [cookies, , removeCookie] = useCookies(['sessionRecording']);
    const userDetailsData = useAppSelector(userDetails);
    const authorizedId = useAppSelector(selectAuthState)?.id;
    const intercomOpen = useRef(false);
    const intercomAutoOpenRetryCount = useRef(0);
    const sendDataToGTM = useGTMDispatch();
    const dispatch = useAppDispatch();
    const micEvents = useAppSelector(selectMicEvents);
    const micModalOpen = useAppSelector(selectMicModalOpen);
    const posthog = usePostHog();
    const top = useAppSelector(selectAccessibilityTop);
    const shutDownIntercom = () => {
        try {
            window.Intercom?.('shutdown');
        } catch (e) {
            console.error('Error shutting down intercom', e)
        }
    }

    const updateIntercomIFrames = useCallback(
        () => {
            if (!top) return;
            const bottom = `${top + 15}px`

            const iframeSelectors = [
                { name: 'intercom-notifications-frame', label: 'intercom-notifications-frame' },
                { name: 'intercom-chat-frame', label: 'intercom-chat-frame' },
                { name: 'intercom-borderless-frame', label: 'intercom-borderless-frame' },
            ];

            iframeSelectors.forEach(({ name, label }) => {
                const iframe = document.querySelector(`iframe[name="${name}"]`) as HTMLIFrameElement;
                if (iframe) {
                    iframe.style.setProperty('bottom', bottom, 'important');
                }
            });
        }
        , [top])


    useEffect(() => {
        updateIntercomIFrames(); // test
    }, [updateIntercomIFrames]);

    useEffect(() => {
        const observer = new MutationObserver((mutationsList) => {
            mutationsList.forEach(mutation => {
                if (mutation.type === 'childList') {
                    updateIntercomIFrames();
                }
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        return () => observer.disconnect();
    }, [updateIntercomIFrames]);

    const shutDownIntercomContext = () => {
        shutDownIntercom();
    }
    const bootIntercomContext = () => {
        shutDownIntercom();
        const bootArgs = {
            app_id: 'kwurgti5'
        };
        window.Intercom?.('boot', { ...bootArgs, ...(authorizedId ? { user_id: authorizedId } : {}) });
    }

    const openIfSessionStorageFlagTrue = useCallback(() => {
        if (sessionStorage.getItem('chatAutoOpen') === 'true') {
            setTimeout(() => {
                // show();
                window.Intercom?.('show');
                intercomAutoOpenRetryCount.current < 30 && setTimeout(() => {
                    if (!intercomOpen.current) {
                        openIfSessionStorageFlagTrue();
                        intercomAutoOpenRetryCount.current += 1;
                    } else sessionStorage.removeItem('chatAutoOpen');
                }, 200);
            }, 1000);
        }
    }, []);

    useEffect(() => {
        const config = { childList: true };
        const observer = new MutationObserver((changes, self) => {
            const addedBlocks = changes.flatMap((item) => (
                [...(item.addedNodes || [])].filter(el => el.nodeName.toUpperCase() === 'DIV') as HTMLDivElement[]
            ));

            if (addedBlocks.some(el => el.classList.contains('intercom-lightweight-app'))) {
                openIfSessionStorageFlagTrue();
                self.disconnect();
            }
        });
        observer.observe(document.body, config);
    }, [openIfSessionStorageFlagTrue]);

    useEffect(() => {
        const isAuthorized = !!authorizedId;
        const userId = authorizedId?.toString() || '';
        if (isAuthorized) {
            shutDownIntercom();
            window.Intercom?.('boot', { ...(userId ? { user_id: userId } : {}) });
            window.Intercom?.('onShow', () => {
                intercomOpen.current = true;
            });
            window.Intercom?.('onHide', () => {
                intercomOpen.current = false;
            })
        } else {
            shutDownIntercom();
        }
    }, [authorizedId]);


    useEffect(() => {
        const isAuthorized = !!authorizedId
        const userId = authorizedId?.toString() || '';
        
        if (!isAuthorized) {
            posthog?.stopSessionRecording()
            removeCookie('sessionRecording');
        } else if (userDetailsData?.email) {
            const sessionRecording = cookies.sessionRecording;
            sendDataToGTM({ event: GtmEvent.RETURN, userId, sessionRecording: !!cookies.sessionRecording });
            posthog?.identify(userId.toString(), { email: userDetailsData?.email });
            if (sessionRecording) {
                posthog?.startSessionRecording()
            }
        }
    }, [authorizedId, cookies.sessionRecording, posthog, removeCookie, sendDataToGTM, userDetailsData?.email]);

    useEffect(() => {
        return () => {
            shutDownIntercom();
            posthog?.stopSessionRecording()
        };
    }, []);

    useEffect(() => {
        const handleBeforeUnload = () => {
            sessionStorage.removeItem("redirectAfterLogin");
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [])

    // if we come to the app with a query param email, we need to reset the auth slice if the email is different
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const email = queryParams.get('email')?.trim().toLowerCase() || '';
        if (email && userDetailsData?.email !== email) {
            resetPersistSlice('auth');
        }
    }, [userDetailsData?.email]);

    return (
        <IntercomContext.Provider value={{ shutDownIntercomContext, bootIntercomContext }}>
            <div>
                <Modal
                    onClose={() => {
                        dispatch(closeModal());
                    }}
                    open={micModalOpen}
                    dataTestId="clear-mic"
                >
                    <button
                        className="mb-5 bg-primary p-3"
                        onClick={() => {
                            dispatch(clearVoiceToSpeechTracking());
                        }}
                        data-testid="button-clear-mic-events"
                    >
                        Clear mic events
                    </button>
                    <div className="flex flex-col gap-4">
                        {micEvents.map((event, index) => (
                            <div key={index}>{event}</div>
                        ))}
                    </div>
                </Modal>
                <RouterProvider router={AppRouter} />
            </div>
        </IntercomContext.Provider>
    );
};
